import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { BaseApiService } from '@app/core/services/api/base-api/base-api.service';
import { LocalizationService } from '@app/core/services/infrastructure/localization/localization.service';
import { environment } from '@environment/environment';
import { API_CONSTANTS } from '@app/core/constants/api.constants';
import { IShareComment, IShareLink, ISharedView } from '../interfaces/share.interface';
import { OnboardingService } from './onboarding.service';

/** Sharing from the calendar: links for plans (co-coaches), weeks and periods (club, parents). */
@Injectable({
    providedIn: 'root',
})
export class ShareApiService extends BaseApiService {
    constructor(
        http: HttpClient,
        private readonly localizationService: LocalizationService,
        private readonly onboardingService: OnboardingService
    ) {
        super(http, API_CONSTANTS.ENDPOINTS.SHARES);
    }

    /** The link preview (WhatsApp, Mail …) is shown in the language the app is in now. */
    private get language(): 'de' | 'en' {
        return this.localizationService.currentLanguage?.startsWith('de') ? 'de' : 'en';
    }

    sharePlan(sessionUuid: string, eventUuid?: string): Observable<IShareLink> {
        return this.post<IShareLink>('/plan', {
            sessionUuid,
            ...(eventUuid ? { eventUuid } : {}),
            language: this.language,
        }).pipe(tap(() => this.onboardingService.refreshIfOpen('planShared')));
    }

    shareWeek(from: string): Observable<IShareLink> {
        return this.post<IShareLink>('/week', { from, language: this.language });
    }

    sharePeriod(periodUuid: string): Observable<IShareLink> {
        return this.post<IShareLink>('/period', { periodUuid, language: this.language });
    }

    /** Several weeks (e.g. a preparation plan); `to` is the exclusive end (a Monday). */
    shareRange(from: string, to: string, title?: string): Observable<IShareLink> {
        return this.post<IShareLink>('/range', { from, to, ...(title ? { title } : {}), language: this.language });
    }

    revoke(token: string): Observable<void> {
        return this.delete<void>(`/${token}`);
    }

    /** Signed in: the complete view; otherwise the public one (a plan only in outline). */
    view(token: string, signedIn: boolean): Observable<ISharedView> {
        return this.get<ISharedView>(signedIn ? `/${token}` : `/${token}/public`);
    }

    takeOver(token: string, date: string): Observable<{ eventUuid: string; sessionUuid: string }> {
        return this.post<{ eventUuid: string; sessionUuid: string }>(`/${token}/take-over`, { date });
    }

    comments(token: string): Observable<{ comments: IShareComment[] }> {
        return this.get<{ comments: IShareComment[] }>(`/${token}/comments`);
    }

    addComment(token: string, text: string): Observable<{ comments: IShareComment[] }> {
        return this.post<{ comments: IShareComment[] }>(`/${token}/comments`, { text });
    }
}

/**
 * The address that is shared. It is a small page of the API with the link preview (title and
 * description in the coach's language) that forwards to `/shared/:token` in the app — link previews
 * never run the app, so they would otherwise only see its default English title.
 */
export function shareUrl(token: string): string {
    return `${environment.API_URL}${API_CONSTANTS.ENDPOINTS.SHARES}/${token}/link`;
}

/** Where to go after signing in, when a coach came from a shared link. */
export const PENDING_REDIRECT_KEY = 'pendingRedirect';
